import { z } from 'zod';
import { EmailDesignSchema } from '../emailDesign';
import { IsoDateSchema, IsoDateTimeSchema, LocaleSchema, UuidSchema } from './common';
import { ChannelSchema } from './messaging';

/**
 * Automations (Pro, spec §10.4–10.5): declarative, versioned workflows — never tenant-authored
 * code. A workflow is `trigger` + ordered `steps` + `settings`; every activated/edited definition
 * is frozen in `workflow_versions` and in-flight runs execute the version they started on.
 *
 * Names are prefixed (Workflow… / Automation…) because every contract is re-exported from the
 * package root.
 */

export const WORKFLOW_STATUSES = ['draft', 'active', 'paused'] as const;
export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number];

export const WORKFLOW_TEMPLATE_KEYS = [
  'birthday',
  'new_year',
  'easter',
  'name_day',
  'win_back',
  'thank_you_purchase',
  'appointment_follow_up',
  'membership_expiry',
  'review_request_after_event',
  'custom',
] as const;
export type WorkflowTemplateKey = (typeof WORKFLOW_TEMPLATE_KEYS)[number];

export const WORKFLOW_RUN_STATUSES = [
  'pending',
  'running',
  'waiting',
  'completed',
  'skipped',
  'failed',
  'canceled',
] as const;
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number];

export const STEP_EXECUTION_STATUSES = ['started', 'succeeded', 'skipped', 'failed'] as const;
export type StepExecutionStatus = (typeof STEP_EXECUTION_STATUSES)[number];

/** Why a workflow is paused (`user` = by a person). */
export const WORKFLOW_PAUSE_REASONS = ['user', 'plan_downgrade'] as const;
export type WorkflowPauseReason = (typeof WORKFLOW_PAUSE_REASONS)[number];

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const WorkflowTimeSchema = z.string().regex(HHMM, 'Use HH:MM (24h)');

/** Longest month lengths (February: 29, a leap-year date). */
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

const minutesOf = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const hhmmOf = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/**
 * Where a local send time `time` lands given quiet-hour windows (start inclusive, end exclusive,
 * may cross midnight, may chain like 21–09 then 09–10): null when outside all of them, else the
 * time sending resumes and whether that is on the next day. The engine waits the same way, and
 * an occasion message (birthday, New Year…) whose wait reaches the next day is never sent.
 */
export function quietHoursResume(
  time: string,
  windows: readonly ({ start: string; end: string } | null | undefined)[],
): { resumesAt: string; nextDay: boolean } | null {
  const list = windows.filter((w): w is { start: string; end: string } => Boolean(w));
  let t = minutesOf(time);
  let moved = false;
  for (let i = 0; i < 6; i++) {
    let step = false;
    for (const w of list) {
      const s = minutesOf(w.start);
      const e = minutesOf(w.end);
      if (s === e) continue;
      const inside = s < e ? t >= s && t < e : t >= s || t < e;
      if (!inside) continue;
      if (s > e && t >= s) return { resumesAt: hhmmOf(e), nextDay: true };
      t = e;
      step = moved = true;
    }
    if (!step) break;
  }
  return moved ? { resumesAt: hhmmOf(t), nextDay: false } : null;
}

/** Feb 29 birthdays/anniversaries in non-leap years: celebrate Feb 28 (default), Mar 1, or skip. */
export const LEAP_DAY_POLICIES = ['feb28', 'mar1', 'skip'] as const;
export type LeapDayPolicy = (typeof LEAP_DAY_POLICIES)[number];

export const INACTIVITY_BASES = ['last_visit', 'last_purchase', 'last_event'] as const;
export type InactivityBasis = (typeof INACTIVITY_BASES)[number];

/** `birth_date` (core column) or `field:<key>` (a `date` custom field on contacts). */
export const WorkflowDateFieldRefSchema = z
  .string()
  .regex(/^(birth_date|field:[a-z][a-z0-9_]{0,63})$/, 'Unknown date field');

const EventTypeKey = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/);

export const WorkflowTriggerSchema = z.discriminatedUnion('type', [
  /** A customer event (purchase_completed, appointment_completed, haircut_completed…). */
  z.object({ type: z.literal('event'), eventType: EventTypeKey }),
  /**
   * A date on the contact: annual anniversary (birthday) or one-off date (membership expiry).
   * `offsetDays` < 0 fires before the date (e.g. -7 = a week before expiry).
   */
  z.object({
    type: z.literal('date_field'),
    field: WorkflowDateFieldRefSchema,
    offsetDays: z.number().int().min(-90).max(90).default(0),
    annual: z.boolean().default(false),
    leapDay: z.enum(LEAP_DAY_POLICIES).default('feb28'),
  }),
  /** A fixed calendar day every year (New Year: 1/1). Feb 29 is allowed (leap years only). */
  z
    .object({
      type: z.literal('calendar'),
      month: z.number().int().min(1).max(12),
      day: z.number().int().min(1).max(31),
    })
    .superRefine((t, ctx) => {
      if (t.day > DAYS_IN_MONTH[t.month - 1]!)
        ctx.addIssue({ code: 'custom', path: ['day'], message: 'This day does not exist' });
    }),
  /** Orthodox Easter (Greek calendar) ± days, e.g. -1 = Holy Saturday. */
  z.object({
    type: z.literal('easter'),
    offsetDays: z.number().int().min(-7).max(7).default(0),
  }),
  /** Greek name day of the contact's first name (curated calendar of common names). */
  z.object({ type: z.literal('name_day') }),
  /** No visit/purchase/event for `days` (contact metrics). `withinDays` ignores long-gone customers. */
  z.object({
    type: z.literal('inactivity'),
    days: z.number().int().min(7).max(730),
    basis: z.enum(INACTIVITY_BASES),
    withinDays: z.number().int().min(8).max(1095).optional(),
  }),
  /** Contact entered a saved segment (evaluated about every 15 minutes). */
  z.object({
    type: z.literal('segment_entry'),
    segmentId: UuidSchema,
    includeExisting: z.boolean().default(false),
  }),
  /** A contact was added (manual / API; imports never trigger). */
  z.object({ type: z.literal('contact_created') }),
]);
export type WorkflowTrigger = z.infer<typeof WorkflowTriggerSchema>;
/** Stored shape (defaults may be absent in jsonb; parse with the schema on read). */
export type WorkflowTriggerInput = z.input<typeof WorkflowTriggerSchema>;
export type WorkflowTriggerType = WorkflowTrigger['type'];

export const WorkflowTextSchema = z
  .object({ el: z.string().max(1600), en: z.string().max(1600) })
  .partial();
export type WorkflowText = z.infer<typeof WorkflowTextSchema>;

/** Built-in predicates for `condition` steps (no expressions, no code). */
export const WORKFLOW_CONDITIONS = [
  /** No visit/purchase (per the trigger basis) since the run started. */
  'not_returned_since_trigger',
  /** Latest review request has no Google click, private feedback or opt-out. */
  'no_review_completion_or_optout',
  /** The contact can still receive marketing on at least one channel. */
  'has_marketing_consent',
] as const;
export type WorkflowCondition = (typeof WORKFLOW_CONDITIONS)[number];

const StepId = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9_-]*$/);

export const WorkflowSendChannelSchema = z.union([ChannelSchema, z.literal('prefer_sms')]);
export type WorkflowSendChannel = z.infer<typeof WorkflowSendChannelSchema>;

export const WorkflowStepSchema = z.discriminatedUnion('type', [
  /** Wait `days`/`hours` from the previous step; `at` aligns to a local time of day. */
  z.object({
    id: StepId,
    type: z.literal('delay'),
    hours: z
      .number()
      .int()
      .min(0)
      .max(24 * 90)
      .optional(),
    days: z.number().int().min(0).max(365).optional(),
    at: WorkflowTimeSchema.optional(),
  }),
  z.object({
    id: StepId,
    type: z.literal('eligibility_check'),
    purpose: z.enum(['marketing', 'review_request']),
  }),
  /** Inline message (one text for both channels). */
  z.object({
    id: StepId,
    type: z.literal('send_message'),
    channel: WorkflowSendChannelSchema,
    subject: WorkflowTextSchema.optional(),
    body: WorkflowTextSchema,
    /** Designed email used when the step sends by email (rendered at send time with merge values). */
    emailDesign: EmailDesignSchema.optional(),
  }),
  /**
   * Message from the template library (`templateKey`, e.g. `birthday.greeting`). `sms`, `email`
   * and `subject` override the library copy per locale (edited in the activation wizard).
   */
  z.object({
    id: StepId,
    type: z.literal('send_template'),
    templateKey: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z_]+\.[a-z_]+$/),
    channel: WorkflowSendChannelSchema,
    sms: WorkflowTextSchema.optional(),
    email: WorkflowTextSchema.optional(),
    subject: WorkflowTextSchema.optional(),
    /** Designed email used when the step sends by email (replaces the plain `email` text). */
    emailDesign: EmailDesignSchema.optional(),
  }),
  /** Create a tracked Google review request through the reviews module (store settings apply). */
  z.object({ id: StepId, type: z.literal('send_review_request') }),
  z.object({ id: StepId, type: z.literal('condition'), if: z.enum(WORKFLOW_CONDITIONS) }),
  z.object({ id: StepId, type: z.literal('add_tag'), tag: z.string().trim().min(1).max(40) }),
  z.object({ id: StepId, type: z.literal('stop'), reason: z.string().max(80).optional() }),
]);
export type WorkflowStep = z.infer<typeof WorkflowStepSchema>;
export type WorkflowStepInput = z.input<typeof WorkflowStepSchema>;
export type WorkflowStepType = WorkflowStep['type'];
export type WorkflowSendStep = Extract<WorkflowStep, { type: 'send_message' | 'send_template' }>;

export const AutomationQuietHoursSchema = z.object({
  start: WorkflowTimeSchema,
  end: WorkflowTimeSchema,
});

export const WorkflowSettingsSchema = z.object({
  /** Narrower/wider quiet hours for this workflow (default: tenant policy → messaging settings). */
  quietHours: AutomationQuietHoursSchema.optional(),
  /** Per-workflow cap: messages per contact per rolling 30 days. */
  maxSendsPerContactPerMonth: z.number().int().min(1).max(30).default(4),
  /** Local time date/calendar/inactivity triggers send at (tenant timezone). */
  sendTime: WorkflowTimeSchema.default('10:00'),
  /** Only contacts with any of these tags (empty = everyone). */
  audienceTags: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
  /** A step due longer ago than this (pause, outage, no credits) is skipped instead of sent late. */
  staleAfterHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 14)
    .default(48),
  /** Optional offer line the wizard added (kept so it can be toggled when editing). */
  offer: WorkflowTextSchema.optional(),
});
export type WorkflowSettings = z.infer<typeof WorkflowSettingsSchema>;
export type WorkflowSettingsInput = z.input<typeof WorkflowSettingsSchema>;

export const SEND_STEP_TYPES = ['send_message', 'send_template', 'send_review_request'] as const;
const SEND_TYPES = new Set<string>(SEND_STEP_TYPES);
export const MAX_SENDS_PER_RUN = 3;

export const WorkflowDefinitionSchema = z
  .object({
    trigger: WorkflowTriggerSchema,
    steps: z.array(WorkflowStepSchema).min(1).max(20),
    settings: WorkflowSettingsSchema.default(() => WorkflowSettingsSchema.parse({})),
  })
  .superRefine((d, ctx) => {
    const seen = new Set<string>();
    d.steps.forEach((s, i) => {
      if (seen.has(s.id))
        ctx.addIssue({
          code: 'custom',
          path: ['steps', i, 'id'],
          message: `Duplicate step id "${s.id}"`,
        });
      seen.add(s.id);
      if (s.type === 'delay' && !s.hours && !s.days && !s.at)
        ctx.addIssue({ code: 'custom', path: ['steps', i], message: 'Delay needs a duration' });
      if (s.type === 'send_message' && !s.body.el?.trim() && !s.body.en?.trim())
        ctx.addIssue({ code: 'custom', path: ['steps', i, 'body'], message: 'Message is empty' });
    });
    const sends = d.steps.filter((s) => SEND_TYPES.has(s.type)).length;
    if (sends === 0)
      ctx.addIssue({ code: 'custom', path: ['steps'], message: 'Add at least one send step' });
    if (sends > MAX_SENDS_PER_RUN)
      ctx.addIssue({
        code: 'custom',
        path: ['steps'],
        message: `At most ${MAX_SENDS_PER_RUN} messages per run`,
      });
    if (
      d.trigger.type === 'inactivity' &&
      d.trigger.withinDays !== undefined &&
      d.trigger.withinDays <= d.trigger.days
    )
      ctx.addIssue({
        code: 'custom',
        path: ['trigger', 'withinDays'],
        message: 'Must be longer than the inactivity period',
      });
  });
export type WorkflowDefinition = z.infer<typeof WorkflowDefinitionSchema>;
export type WorkflowDefinitionInput = z.input<typeof WorkflowDefinitionSchema>;

/** Tenant-wide automation policy (applies across every workflow). */
export const AutomationPolicySchema = z.object({
  /** Max automation messages one contact gets in a rolling 7 days (all workflows). */
  maxPerContactPerWeek: z.number().int().min(1).max(14).default(2),
  /** Max automation messages one contact gets in a rolling 30 days (all workflows). */
  maxPerContactPerMonth: z.number().int().min(1).max(30).default(4),
  /** Overrides the business's quiet hours for automations (null = messaging settings). */
  quietHours: AutomationQuietHoursSchema.nullable().default(null),
  /** Default Feb 29 policy for new date workflows. */
  leapDay: z.enum(LEAP_DAY_POLICIES).default('feb28'),
  /** Emergency brake: nothing is sent while true (runs wait; stale ones are skipped). */
  pauseAll: z.boolean().default(false),
});
export type AutomationPolicy = z.infer<typeof AutomationPolicySchema>;

// ─── template library ─────────────────────────────────────────────────────────

export const AUTOMATION_PREREQUISITE_KEYS = [
  'contacts',
  'birthdays',
  'phone_or_email',
  'marketing_consent',
  'purchase_events',
  'visit_events',
  'appointment_events',
  'membership_field',
  'review_link',
  'name_days',
] as const;
export type AutomationPrerequisiteKey = (typeof AUTOMATION_PREREQUISITE_KEYS)[number];

export const AutomationPrerequisiteSchema = z.object({
  key: z.enum(AUTOMATION_PREREQUISITE_KEYS),
  status: z.enum(['ok', 'partial', 'missing']),
  /** e.g. contacts with a birthday. */
  count: z.number().int(),
  /** Out of (e.g. active contacts). */
  total: z.number().int().nullable(),
  required: z.boolean(),
  /** Where the user fixes it. */
  fix: z
    .object({
      kind: z.enum(['import', 'contacts', 'data_schema', 'review_settings', 'consent']),
      href: z.string(),
    })
    .nullable(),
});
export type AutomationPrerequisite = z.infer<typeof AutomationPrerequisiteSchema>;

export const AUTOMATION_TEMPLATE_CATEGORIES = ['seasonal', 'lifecycle', 'after_visit'] as const;
export type AutomationTemplateCategory = (typeof AUTOMATION_TEMPLATE_CATEGORIES)[number];

export const AUTOMATION_BLOCKED_REASONS = [
  'no_contacts',
  'no_birthdays',
  'no_name_days',
  'no_activity_history',
  'no_purchase_events',
  'no_appointment_events',
  'no_membership_field',
  'membership_field_empty',
] as const;
export type AutomationBlockedReason = (typeof AUTOMATION_BLOCKED_REASONS)[number];

/** Library copy of one message (Greek-first, formal; platform-overridable). */
export const AutomationMessageCopySchema = z.object({
  sms: WorkflowTextSchema,
  email: WorkflowTextSchema,
  subject: WorkflowTextSchema,
});
export type AutomationMessageCopy = z.infer<typeof AutomationMessageCopySchema>;

export const AutomationTemplateSchema = z.object({
  key: z.enum(WORKFLOW_TEMPLATE_KEYS),
  category: z.enum(AUTOMATION_TEMPLATE_CATEGORIES),
  /** Fields the template needs (keys for the explainer). */
  requiredFields: z.array(z.string()),
  prerequisites: z.array(AutomationPrerequisiteSchema),
  /** All required prerequisites are met. */
  ready: z.boolean(),
  /**
   * Why the template's default definition cannot run yet for this tenant. Advisory: the wizard
   * re-checks it against the trigger the user picks (`draftBlockedReason`); the API does not
   * refuse activation (events may start arriving from an integration later).
   */
  blockedReason: z.enum(AUTOMATION_BLOCKED_REASONS).nullable(),
  defaultName: WorkflowTextSchema,
  /** Ready-to-activate definition with the business's best guesses (event type, field…). */
  definition: z.object({
    trigger: WorkflowTriggerSchema,
    steps: z.array(WorkflowStepSchema),
    settings: WorkflowSettingsSchema,
  }),
  /** Optional offer line (Greek-first) the wizard can append. */
  offer: WorkflowTextSchema.nullable(),
  /** Picked during AI onboarding ("to set up") and not set up yet. */
  suggested: z.boolean().default(false),
  /** Existing workflows created from this template. */
  workflows: z.array(
    z.object({ id: UuidSchema, name: z.string(), status: z.enum(WORKFLOW_STATUSES) }),
  ),
});
export type AutomationTemplate = z.infer<typeof AutomationTemplateSchema>;

/**
 * The blocked reason for what the user actually picked: the template's default guess (event
 * type, date field) is replaced by the chosen one, checked against the tenant's data.
 */
export function draftBlockedReason(
  template: Pick<AutomationTemplate, 'key' | 'blockedReason'>,
  trigger: WorkflowTrigger,
  data: {
    eventTypes: readonly { key: string }[];
    dateFields: readonly { ref: string; filled: number }[];
  },
): AutomationBlockedReason | null {
  if (trigger.type === 'event') {
    if (data.eventTypes.some((e) => e.key === trigger.eventType)) return null;
    if (template.key === 'thank_you_purchase') return 'no_purchase_events';
    if (template.key === 'appointment_follow_up') return 'no_appointment_events';
    return null;
  }
  if (template.key === 'membership_expiry' && trigger.type === 'date_field') {
    const f = data.dateFields.find((d) => d.ref === trigger.field);
    if (!f) return 'no_membership_field';
    return f.filled > 0 ? null : 'membership_field_empty';
  }
  return template.blockedReason;
}

export const AutomationTemplatesResponseSchema = z.object({
  templates: z.array(AutomationTemplateSchema),
  /** Library copy by message key (`birthday.greeting`…). */
  messages: z.record(z.string(), AutomationMessageCopySchema),
  timezone: z.string(),
  activeCount: z.number().int(),
  activeLimit: z.number().int().nullable(),
});
export type AutomationTemplatesResponse = z.infer<typeof AutomationTemplatesResponseSchema>;

/** Data the wizard offers for triggers (date fields, event types, segments, tags). */
export const AutomationFieldsResponseSchema = z.object({
  dateFields: z.array(z.object({ ref: z.string(), label: z.string(), filled: z.number().int() })),
  eventTypes: z.array(
    z.object({ key: z.string(), label: z.string(), count: z.number().int(), visit: z.boolean() }),
  ),
  segments: z.array(z.object({ id: UuidSchema, name: z.string() })),
  /** False until the segments module registers its audience resolver. */
  segmentsAvailable: z.boolean(),
  tags: z.array(z.object({ tag: z.string(), count: z.number().int() })),
  nextEaster: IsoDateSchema,
  policy: AutomationPolicySchema,
  /**
   * The business's quiet hours (messaging settings). Automations also respect `policy.quietHours`
   * and the workflow's own window: all of them apply together.
   */
  quietHours: AutomationQuietHoursSchema,
  timezone: z.string(),
});
export type AutomationFieldsResponse = z.infer<typeof AutomationFieldsResponseSchema>;

// ─── workflows ────────────────────────────────────────────────────────────────

export const WorkflowStatsSchema = z.object({
  inFlight: z.number().int(),
  completed: z.number().int(),
  skipped: z.number().int(),
  failed: z.number().int(),
  sent30d: z.number().int(),
  nextActionAt: IsoDateTimeSchema.nullable(),
  lastRunAt: IsoDateTimeSchema.nullable(),
});
export type WorkflowStats = z.infer<typeof WorkflowStatsSchema>;

export const WorkflowSchema = z.object({
  id: UuidSchema,
  templateKey: z.enum(WORKFLOW_TEMPLATE_KEYS),
  name: z.string(),
  description: z.string().nullable(),
  status: z.enum(WORKFLOW_STATUSES),
  trigger: WorkflowTriggerSchema,
  steps: z.array(WorkflowStepSchema),
  settings: WorkflowSettingsSchema,
  version: z.number().int(),
  activatedAt: IsoDateTimeSchema.nullable(),
  pausedReason: z.enum(WORKFLOW_PAUSE_REASONS).nullable(),
  /** Last trigger-detection problem (e.g. the date field was archived). */
  lastError: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  stats: WorkflowStatsSchema,
});
export type Workflow = z.infer<typeof WorkflowSchema>;

export const WorkflowListResponseSchema = z.object({
  items: z.array(WorkflowSchema),
  activeLimit: z.number().int().nullable(),
  policy: AutomationPolicySchema,
});
export type WorkflowListResponse = z.infer<typeof WorkflowListResponseSchema>;

const NameSchema = z.string().trim().min(1).max(120);

export const CreateWorkflowRequestSchema = z.object({
  templateKey: z.enum(WORKFLOW_TEMPLATE_KEYS),
  name: NameSchema,
  description: z.string().trim().max(500).nullable().optional(),
  definition: WorkflowDefinitionSchema,
  /** Activate right away (admin+, needs an active subscription). */
  activate: z.boolean().default(false),
});
export type CreateWorkflowRequest = z.input<typeof CreateWorkflowRequestSchema>;

export const UpdateWorkflowRequestSchema = z
  .object({
    name: NameSchema.optional(),
    description: z.string().trim().max(500).nullable().optional(),
    definition: WorkflowDefinitionSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type UpdateWorkflowRequest = z.input<typeof UpdateWorkflowRequestSchema>;

export const WorkflowStepExecutionSchema = z.object({
  stepId: z.string(),
  stepIndex: z.number().int(),
  status: z.enum(STEP_EXECUTION_STATUSES),
  result: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  startedAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema.nullable(),
});
export type WorkflowStepExecution = z.infer<typeof WorkflowStepExecutionSchema>;

export const WorkflowRunSchema = z.object({
  id: UuidSchema,
  workflowVersion: z.number().int(),
  contact: z.object({ id: UuidSchema, name: z.string() }).nullable(),
  triggerOccurrenceId: z.string(),
  status: z.enum(WORKFLOW_RUN_STATUSES),
  currentStep: z.number().int(),
  nextActionAt: IsoDateTimeSchema.nullable(),
  attempts: z.number().int(),
  lastError: z.string().nullable(),
  outcome: z.record(z.string(), z.unknown()).nullable(),
  startedAt: IsoDateTimeSchema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  steps: z.array(WorkflowStepExecutionSchema),
});
export type WorkflowRun = z.infer<typeof WorkflowRunSchema>;

export const WorkflowRunsQuerySchema = z.object({
  status: z.enum(['all', 'in_flight', 'completed', 'skipped', 'failed']).default('all'),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const WorkflowRunsResponseSchema = z.object({
  items: z.array(WorkflowRunSchema),
  nextCursor: z.string().nullable(),
});
export type WorkflowRunsResponse = z.infer<typeof WorkflowRunsResponseSchema>;

export const WorkflowVersionSchema = z.object({
  version: z.number().int(),
  trigger: WorkflowTriggerSchema,
  steps: z.array(WorkflowStepSchema),
  settings: WorkflowSettingsSchema,
  createdBy: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  runs: z.number().int(),
});
export type WorkflowVersion = z.infer<typeof WorkflowVersionSchema>;
export const WorkflowVersionsResponseSchema = z.object({ items: z.array(WorkflowVersionSchema) });

// ─── estimate / preview ───────────────────────────────────────────────────────

export const AutomationEstimateRequestSchema = z.object({
  trigger: WorkflowTriggerSchema,
  channel: WorkflowSendChannelSchema.default('prefer_sms'),
  audienceTags: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
  /** SMS text used for the units estimate (rendered with a sample name). */
  smsBody: z.string().max(1600).optional(),
  /** Messages per run (win-back = 2). */
  sendsPerRun: z.number().int().min(1).max(MAX_SENDS_PER_RUN).default(1),
});
export type AutomationEstimateRequest = z.input<typeof AutomationEstimateRequestSchema>;

export const AutomationEstimateSchema = z.object({
  /** Active contacts in the audience (after tag filter). */
  audience: z.number().int(),
  /** …that have the data the trigger needs (birthday, recognised name, activity…). */
  withData: z.number().int(),
  /** …matching now (inactivity) or in the next 30 days (date triggers); null for events. */
  matching: z.number().int().nullable(),
  /** …of which can be reached with marketing permission (address, consent/basis, not suppressed). */
  reachable: z.object({ sms: z.number().int(), email: z.number().int(), any: z.number().int() }),
  /** Next 30 days by local date (date/calendar triggers). */
  upcoming: z.array(z.object({ date: IsoDateSchema, count: z.number().int() })),
  nextOccurrence: IsoDateSchema.nullable(),
  /** Events of this type in the last 30 days (event triggers). */
  eventsLast30d: z.number().int().nullable(),
  smsUnitsPerMessage: z.number().int().nullable(),
  /** Rough credit use over the next 30 days. */
  monthlyCredits: z.object({ sms: z.number().int(), email: z.number().int() }),
  /** Trigger problems (unknown date field, segments unavailable…). */
  warnings: z.array(z.string()),
});
export type AutomationEstimate = z.infer<typeof AutomationEstimateSchema>;

export const AutomationPreviewRequestSchema = z.object({
  channel: ChannelSchema,
  subject: z.string().max(200).optional(),
  body: z.string().min(1).max(1600),
  contactId: UuidSchema.optional(),
  locale: LocaleSchema.optional(),
});
export type AutomationPreviewRequest = z.input<typeof AutomationPreviewRequestSchema>;
export const AutomationPreviewSchema = z.object({
  text: z.string(),
  subject: z.string().nullable(),
  units: z.number().int(),
  encoding: z.enum(['GSM-7', 'UCS-2']).nullable(),
  missing: z.array(z.string()),
  unknown: z.array(z.string()),
  sampleName: z.string().nullable(),
  sender: z.string(),
});
export type AutomationPreview = z.infer<typeof AutomationPreviewSchema>;

export const AutomationPolicyResponseSchema = z.object({ policy: AutomationPolicySchema });
export const UpdateAutomationPolicyRequestSchema = AutomationPolicySchema.partial();
export type UpdateAutomationPolicyRequest = z.input<typeof UpdateAutomationPolicyRequestSchema>;

// ─── AI copy suggestion (Pro `ai.copy`) ───────────────────────────────────────

export const AUTOMATION_COPY_TONES = ['warm', 'brief', 'playful'] as const;
export const AutomationCopyRequestSchema = z.object({
  /** Library message key, e.g. `birthday.greeting`. */
  messageKey: z.string().regex(/^[a-z_]+\.[a-z_]+$/),
  tone: z.enum(AUTOMATION_COPY_TONES).default('warm'),
  /** The user's current SMS draft (untrusted; a style reference only). */
  current: z.string().max(1600).optional(),
  /** Offer line to keep verbatim. */
  offer: z.string().max(200).optional(),
  /** Client idempotency (one AI run per click). */
  requestId: z.string().min(8).max(100).optional(),
});
export type AutomationCopyRequest = z.input<typeof AutomationCopyRequestSchema>;
export const AutomationCopyResponseSchema = z.object({
  sms: z.string(),
  subject: z.string(),
  email: z.string(),
  creditsUsed: z.number().int(),
  /** Links the model put in and the server removed (shown to the user). */
  removedLinks: z.array(z.string()).default([]),
});
export type AutomationCopyResponse = z.infer<typeof AutomationCopyResponseSchema>;
